import { useState } from 'react';
import { useCreateQuiz } from '@/api/course-builder';
import { FormShell } from '@/components/shared/form-shell';
import type { CreateQuizInput } from '@/types';
import { inputCls, btnPrimary, btnSecondary, labelCls } from './constants';

export function CreateQuizForm({
  lessonId,
  courseId,
  onClose,
}: {
  lessonId: number;
  courseId: number;
  onClose: () => void;
}) {
  const createQuiz = useCreateQuiz();
  const [form, setForm] = useState<CreateQuizInput>({
    title: '',
    passing_percent: 70,
    time_limit_minutes: undefined,
    max_attempts: 3,
    randomize_questions: false,
    show_correct_answers: true,
  });

  const set = (patch: Partial<CreateQuizInput>) => setForm((f) => ({ ...f, ...patch }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    try {
      await createQuiz.mutateAsync({ lessonId, courseId, data: form });
      onClose();
    } catch {
      void 0;
    }
  };

  return (
    <FormShell title="Crear evaluación" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className={labelCls}>Título</label>
          <input
            className={inputCls}
            value={form.title}
            onChange={(e) => set({ title: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Nota aprobatoria (%)</label>
            <input
              className={inputCls}
              type="number"
              min={0}
              max={100}
              value={form.passing_percent ?? 70}
              onChange={(e) => set({ passing_percent: +e.target.value })}
            />
          </div>
          <div>
            <label className={labelCls}>Intentos max</label>
            <input
              className={inputCls}
              type="number"
              min={1}
              max={10}
              value={form.max_attempts ?? 3}
              onChange={(e) => set({ max_attempts: +e.target.value })}
            />
          </div>
        </div>
        <div>
          <label className={labelCls}>Limite de tiempo (min, opcional)</label>
          <input
            className={inputCls}
            type="number"
            min={1}
            value={form.time_limit_minutes ?? ''}
            onChange={(e) =>
              set({ time_limit_minutes: e.target.value ? +e.target.value : undefined })
            }
          />
        </div>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              className="rounded border-input"
              checked={form.randomize_questions}
              onChange={(e) => set({ randomize_questions: e.target.checked })}
            />
            Aleatorizar preguntas
          </label>
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              className="rounded border-input"
              checked={form.show_correct_answers}
              onChange={(e) => set({ show_correct_answers: e.target.checked })}
            />
            Mostrar respuestas
          </label>
        </div>
        {createQuiz.error && <p className="text-xs text-destructive">{createQuiz.error.message}</p>}
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className={btnSecondary}>
            Cancelar
          </button>
          <button type="submit" disabled={createQuiz.isPending} className={btnPrimary}>
            {createQuiz.isPending ? 'Creando...' : 'Crear evaluación'}
          </button>
        </div>
      </form>
    </FormShell>
  );
}
